import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createId,
  posterUrl,
  type DiscFormat,
  type Movie,
  type TmdbMovieDetails,
  type TmdbSearchResult,
} from '@physical-movies/core';
import { useCollection, useI18n, useTmdb, useToast } from '../app/context';
import { FormatChoice } from '../components/FormatChoice';
import { Icon } from '../components/Icon';
import { PageHeader } from '../components/Page';
import { Sheet } from '../components/Sheet';
import { Button, Field, SearchInput, TextInput } from '../components/ui';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { useScrollToTop } from '../lib/useScrollRestoration';
import styles from './add.module.css';

/**
 * Kept outside the component so it survives leaving this screen and coming
 * back, which is the common case: open Add, look, go away, open it again.
 */
const trendingCache = new Map<string, TmdbSearchResult[]>();

/**
 * How many suggestions to show, and how deep into the trending list to look
 * for them. The gap is the slack for dropping what is already on the shelf.
 */
const SUGGESTION_COUNT = 12;
const TRENDING_DEPTH = 20;

/** One row of the two lists on this screen: search results and suggestions. */
function ResultRow({
  result,
  owned,
  onSelect,
}: {
  result: TmdbSearchResult;
  owned: boolean;
  onSelect(): void;
}) {
  const { t } = useI18n();
  const poster = posterUrl(result.posterPath, 'w154');

  return (
    <button type="button" className={styles.result} onClick={onSelect}>
      <span className={styles.resultPoster}>
        {poster && <img src={poster} alt="" loading="lazy" decoding="async" />}
      </span>
      <span className={styles.resultText}>
        <span className={styles.resultTitle}>{result.title}</span>
        <span className={styles.resultMeta}>{result.year ?? '—'}</span>
      </span>
      {owned ? (
        <span className={styles.owned}>{t('add.alreadyOwned')}</span>
      ) : (
        <Icon name="chevronRight" size={16} className={styles.chevron} />
      )}
    </button>
  );
}

export function AddMovie() {
  useScrollToTop();
  const { t, locale } = useI18n();
  const tmdb = useTmdb();
  const toast = useToast();
  const collection = useCollection();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [manual, setManual] = useState(!tmdb.enabled);
  const [chosen, setChosen] = useState<TmdbSearchResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * The last finished search, tagged with what it was a search *for*. Whether
   * a request is in flight is then derived from that tag rather than tracked
   * in a second piece of state that could disagree with it.
   */
  const [completed, setCompleted] = useState<{
    for: string;
    results: TmdbSearchResult[];
    error: string | null;
  }>({ for: '', results: [], error: null });

  /** Same shape, tagged with the language it was fetched for. */
  const [trending, setTrending] = useState<{
    for: string;
    items: TmdbSearchResult[];
    error: string | null;
  }>({ for: '', items: [], error: null });

  const debouncedQuery = useDebouncedValue(query);
  const trimmedQuery = debouncedQuery.trim();
  const searchKey = `${locale}:${trimmedQuery}`;

  const settled = completed.for === searchKey;
  const results = settled ? completed.results : [];
  const searchError = settled ? completed.error : null;
  const searching = tmdb.enabled && trimmedQuery !== '' && !settled;

  const ownedByTmdbId = useMemo(() => {
    const map = new Map<number, Movie>();
    for (const movie of collection.movies) {
      if (movie.tmdbId !== null) map.set(movie.tmdbId, movie);
    }
    return map;
  }, [collection.movies]);

  /**
   * Trending is a weekly list and costs a request per film to filter, so it is
   * fetched once per language and kept for the tab rather than on every visit
   * to this screen.
   */
  const cachedTrending = trendingCache.get(locale);
  const trendingSettled = trending.for === locale;
  const trendingItems = useMemo(
    () => (trendingSettled ? trending.items : (cachedTrending ?? [])),
    [trendingSettled, trending.items, cachedTrending],
  );
  const trendingError = trendingSettled ? trending.error : null;
  const trendingLoading =
    tmdb.enabled && !manual && !trendingSettled && cachedTrending === undefined;

  useEffect(() => {
    if (!tmdb.enabled || manual) return;
    if (trending.for === locale || trendingCache.has(locale)) return;

    const controller = new AbortController();

    tmdb
      .trendingCinemaMovies({ locale, signal: controller.signal, limit: TRENDING_DEPTH })
      .then((found) => {
        trendingCache.set(locale, found);
        setTrending({ for: locale, items: found, error: null });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setTrending({ for: locale, items: [], error: t('add.trending.failed') });
      });

    return () => controller.abort();
  }, [tmdb, locale, manual, trending.for, t]);

  useEffect(() => {
    if (!tmdb.enabled || !trimmedQuery) return;

    const controller = new AbortController();

    tmdb
      .searchMovies(trimmedQuery, { locale, signal: controller.signal })
      .then((found) => setCompleted({ for: searchKey, results: found, error: null }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setCompleted({ for: searchKey, results: [], error: t('error.generic') });
      });

    return () => controller.abort();
  }, [tmdb, trimmedQuery, searchKey, locale, t]);

  const openConfirm = (result: TmdbSearchResult) => {
    setChosen(result);
    setConfirmOpen(true);
  };

  // Suggestions are worth making only for something not already owned or
  // already wanted — the point is to find something new.
  const suggestions = useMemo(() => {
    const wanted = new Set(
      collection.wishlist
        .map((item) => item.tmdbId)
        .filter((tmdbId): tmdbId is number => tmdbId !== null),
    );
    return trendingItems
      .filter((item) => !ownedByTmdbId.has(item.tmdbId) && !wanted.has(item.tmdbId))
      .slice(0, SUGGESTION_COUNT);
  }, [trendingItems, ownedByTmdbId, collection.wishlist]);

  return (
    <div className={styles.page}>
      <PageHeader title={t('add.title')} />

      {tmdb.enabled && !manual && (
        <>
          <div className={styles.searchBlock}>
            <SearchInput
              value={query}
              onValueChange={setQuery}
              placeholder={t('add.searchPlaceholder')}
              label={t('add.searchPlaceholder')}
              clearLabel={t('common.clear')}
            />
            <p className={styles.hint}>
              {searching ? t('add.searching') : (searchError ?? t('add.searchHint'))}
            </p>
          </div>

          {results.length > 0 && (
            <div className={styles.results}>
              {results.map((result) => (
                <ResultRow
                  key={result.tmdbId}
                  result={result}
                  owned={ownedByTmdbId.has(result.tmdbId)}
                  onSelect={() => openConfirm(result)}
                />
              ))}
            </div>
          )}

          {settled && trimmedQuery !== '' && results.length === 0 && !searchError && (
            <p className={styles.notice}>{t('add.noResults')}</p>
          )}

          {/* With nothing typed, the screen would otherwise be an empty box. */}
          {trimmedQuery === '' && (
            <section className={styles.trending}>
              <h2 className={`eyebrow ${styles.trendingTitle}`}>{t('add.trending.title')}</h2>
              <p className={styles.hint}>{t('add.trending.hint')}</p>

              {trendingLoading && <p className={styles.notice}>{t('add.trending.loading')}</p>}
              {trendingError && <p className={styles.notice}>{trendingError}</p>}
              {!trendingLoading && !trendingError && suggestions.length === 0 && (
                <p className={styles.notice}>{t('add.trending.empty')}</p>
              )}

              {suggestions.length > 0 && (
                <div className={styles.results}>
                  {suggestions.map((result) => (
                    <ResultRow
                      key={result.tmdbId}
                      result={result}
                      owned={false}
                      onSelect={() => openConfirm(result)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          <div className={styles.manualLink}>
            <Button variant="ghost" onClick={() => setManual(true)}>
              {t('add.manual')}
            </Button>
          </div>
        </>
      )}

      {!tmdb.enabled && <p className={styles.notice}>{t('add.tmdbDisabled')}</p>}

      {manual && (
        <ManualForm
          onDone={(id) => void navigate(`/movie/${id}`)}
          onCancel={tmdb.enabled ? () => setManual(false) : undefined}
        />
      )}

      <ConfirmSheet
        open={confirmOpen}
        result={chosen}
        owned={chosen ? (ownedByTmdbId.get(chosen.tmdbId) ?? null) : null}
        onClose={() => setConfirmOpen(false)}
        onAdded={(id) => {
          setConfirmOpen(false);
          toast.show(t('add.added', { title: chosen?.title ?? '' }));
          void navigate(`/movie/${id}`);
        }}
        onWishlisted={() => {
          const title = chosen?.title ?? '';
          setConfirmOpen(false);
          toast.show(t('wishlist.added', { title }));
        }}
      />
    </div>
  );
}

/** Pick the format, then add the disc — or park it on the wishlist. */
function ConfirmSheet({
  open,
  result,
  owned,
  onClose,
  onAdded,
  onWishlisted,
}: {
  open: boolean;
  result: TmdbSearchResult | null;
  owned: Movie | null;
  onClose(): void;
  onAdded(movieId: string): void;
  onWishlisted(): void;
}) {
  const { t } = useI18n();
  const tmdb = useTmdb();
  const collection = useCollection();
  const navigate = useNavigate();

  const [format, setFormat] = useState<DiscFormat>('hd');
  const [busy, setBusy] = useState(false);
  /** Tagged with the film it belongs to, so a slow response cannot land on the wrong one. */
  const [loaded, setLoaded] = useState<{ tmdbId: number; details: TmdbMovieDetails | null } | null>(
    null,
  );

  const current = result && loaded?.tmdbId === result.tmdbId ? loaded : null;
  const details = current?.details ?? null;
  const failed = current !== null && current.details === null;
  const loading = result !== null && current === null;

  useEffect(() => {
    if (!result) return;

    const tmdbId = result.tmdbId;
    const controller = new AbortController();

    tmdb
      .getMovie(tmdbId, { signal: controller.signal })
      .then((fetched) => setLoaded({ tmdbId, details: fetched }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoaded({ tmdbId, details: null });
      });

    return () => controller.abort();
  }, [result, tmdb]);

  const addToCollection = async () => {
    if (!details) return;
    setBusy(true);
    try {
      const id = await collection.addMovie({
        tmdbId: details.tmdbId,
        title: details.title,
        titleDe: details.titleDe,
        originalTitle: details.originalTitle,
        year: details.year,
        releaseDate: details.releaseDate,
        runtime: details.runtime,
        posterPath: details.posterPath,
        backdropPath: details.backdropPath,
        overviewEn: details.overviewEn,
        overviewDe: details.overviewDe,
        genreIds: details.genreIds,
        directors: details.directors,
        copies: [
          { id: createId(), format, edition: null, notes: null, purchasedAt: null, loan: null },
        ],
      });
      onAdded(id);
    } finally {
      setBusy(false);
    }
  };

  const addAnotherCopy = async () => {
    if (!owned) return;
    setBusy(true);
    try {
      await collection.setCopies(owned, [
        ...owned.copies,
        { id: createId(), format, edition: null, notes: null, purchasedAt: null, loan: null },
      ]);
      onAdded(owned.id);
    } finally {
      setBusy(false);
    }
  };

  const addToWishlist = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await collection.addWishlistItem({
        tmdbId: result.tmdbId,
        title: details?.title ?? result.title,
        titleDe: details?.titleDe ?? null,
        year: result.year,
        releaseDate: details?.releaseDate ?? result.releaseDate,
        posterPath: result.posterPath,
        runtime: details?.runtime ?? null,
        desiredFormat: format,
        genreIds: details?.genreIds ?? [],
        note: null,
      });
      onWishlisted();
    } finally {
      setBusy(false);
    }
  };

  const poster = posterUrl(result?.posterPath ?? null, 'w154');

  return (
    <Sheet
      open={open && result !== null}
      onClose={onClose}
      title={result?.title ?? ''}
      closeLabel={t('common.close')}
      footer={
        <div className={styles.footerStack}>
          <Button
            variant="primary"
            block
            disabled={busy || loading || (failed && !owned)}
            onClick={() => void (owned ? addAnotherCopy() : addToCollection())}
          >
            {busy ? t('common.saving') : owned ? t('add.addAnotherCopy') : t('add.confirm')}
          </Button>
          {owned ? (
            <Button block onClick={() => void navigate(`/movie/${owned.id}`)}>
              {t('add.openExisting')}
            </Button>
          ) : (
            <Button block disabled={busy} onClick={() => void addToWishlist()}>
              {t('add.toWishlist')}
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.confirmBody}>
        <div className={styles.confirmHead}>
          <span className={styles.confirmPoster}>
            {poster && <img src={poster} alt="" decoding="async" />}
          </span>
          <span className={styles.resultText}>
            <span className={styles.resultTitle}>{result?.title}</span>
            <span className={styles.resultMeta}>{result?.year ?? '—'}</span>
          </span>
        </div>

        {owned && <p className={styles.ownedNotice}>{t('add.alreadyOwned')}</p>}
        {failed && !owned && <p className={styles.ownedNotice}>{t('add.loadFailed')}</p>}

        <FormatChoice value={format} onChange={setFormat} label={t('add.chooseFormat')} />
      </div>
    </Sheet>
  );
}

/** For box sets, bootlegs and anything TMDB has never heard of. */
function ManualForm({
  onDone,
  onCancel,
}: {
  onDone(movieId: string): void;
  onCancel?: () => void;
}) {
  const { t } = useI18n();
  const collection = useCollection();
  const fieldId = useId();
  const titleRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [titleDe, setTitleDe] = useState('');
  const [year, setYear] = useState('');
  const [runtime, setRuntime] = useState('');
  const [director, setDirector] = useState('');
  const [format, setFormat] = useState<DiscFormat>('hd');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const submit = async () => {
    if (!title.trim()) {
      setError(t('add.titleRequired'));
      return;
    }
    setBusy(true);
    try {
      const id = await collection.addMovie({
        tmdbId: null,
        title: title.trim(),
        titleDe: titleDe.trim() || null,
        originalTitle: null,
        year: year ? Number(year) : null,
        runtime: runtime ? Number(runtime) : null,
        directors: director.trim() ? [director.trim()] : [],
        genreIds: [],
        copies: [
          { id: createId(), format, edition: null, notes: null, purchasedAt: null, loan: null },
        ],
      });
      onDone(id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.manualForm}>
      <Field label={t('add.field.title')} htmlFor={`${fieldId}-title`} error={error}>
        <TextInput
          id={`${fieldId}-title`}
          ref={titleRef}
          value={title}
          maxLength={200}
          onChange={(event) => {
            setTitle(event.target.value);
            setError(null);
          }}
        />
      </Field>

      <Field
        label={t('add.field.titleDe')}
        optional={t('common.optional')}
        htmlFor={`${fieldId}-titleDe`}
      >
        <TextInput
          id={`${fieldId}-titleDe`}
          value={titleDe}
          maxLength={200}
          onChange={(event) => setTitleDe(event.target.value)}
        />
      </Field>

      <div className={styles.formRow}>
        <Field
          label={t('add.field.year')}
          optional={t('common.optional')}
          htmlFor={`${fieldId}-year`}
        >
          <TextInput
            id={`${fieldId}-year`}
            type="number"
            inputMode="numeric"
            min={1880}
            max={2100}
            value={year}
            onChange={(event) => setYear(event.target.value)}
          />
        </Field>
        <Field
          label={t('add.field.runtime')}
          optional={t('common.optional')}
          htmlFor={`${fieldId}-runtime`}
        >
          <TextInput
            id={`${fieldId}-runtime`}
            type="number"
            inputMode="numeric"
            min={1}
            max={1000}
            value={runtime}
            onChange={(event) => setRuntime(event.target.value)}
          />
        </Field>
      </div>

      <Field
        label={t('add.field.director')}
        optional={t('common.optional')}
        htmlFor={`${fieldId}-director`}
      >
        <TextInput
          id={`${fieldId}-director`}
          value={director}
          maxLength={120}
          onChange={(event) => setDirector(event.target.value)}
        />
      </Field>

      <FormatChoice value={format} onChange={setFormat} label={t('add.chooseFormat')} />

      <Button variant="primary" size="lg" block disabled={busy} onClick={() => void submit()}>
        {busy ? t('common.saving') : t('add.confirm')}
      </Button>
      {onCancel && (
        <Button variant="ghost" block onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      )}
    </div>
  );
}
